import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Github, ExternalLink } from "lucide-react";

export default function Android() {
  const [tab, setTab] = useState("sdk");

  return (
    <div>
      <section className="bg-[#f4f5f7] py-14 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h1 className="text-4xl font-semibold text-slate-800">Android</h1>
        </div>
      </section>

      <article className="max-w-5xl mx-auto px-6 lg:px-10 py-14">
        <Link
          to="/development-kits"
          className="inline-flex items-center gap-1 text-sm text-[#29abe2] hover:underline mb-8"
        >
          <ChevronLeft className="w-4 h-4" /> Back to Development Kits
        </Link>

        <div className="border-b border-slate-200 flex gap-1">
          {[
            { id: "sdk", label: "SDK" },
            { id: "apps", label: "Apps" },
            { id: "docs", label: "Documentation" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-6 py-3 text-sm font-semibold uppercase tracking-wide transition-colors ${
                tab === t.id
                  ? "text-[#29abe2] border-b-2 border-[#29abe2] -mb-px"
                  : "text-slate-600 hover:text-slate-800"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="py-10">
          {tab === "sdk" && <SdkTab />}
          {tab === "apps" && <AppsTab />}
          {tab === "docs" && <DocsTab />}
        </div>
      </article>
    </div>
  );
}

function GitHubBadge({ href }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold px-5 py-2.5 rounded transition-colors"
    >
      <Github className="w-4 h-4" />
      Get source on GitHub
    </a>
  );
}

function PlayStoreBadge({ href }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 bg-[#29abe2] hover:bg-[#1f9bd0] text-white text-sm font-semibold px-5 py-2.5 rounded transition-colors"
    >
      <ExternalLink className="w-4 h-4" />
      Get it on Google Play
    </a>
  );
}

function SdkTab() {
  return (
    <div className="space-y-10">
      <section>
        <h2 className="text-2xl md:text-3xl font-semibold text-slate-800 mb-4">Android SDK</h2>
        <p className="text-slate-700 leading-relaxed text-[16px]">
          The Tappy Android SDK supports both TappyBLE and TappyUSB devices. In order to use the SDK, your device
          must support Android version 4.4 or newer. Additionally, the phone must support BLE (also known as
          Bluetooth Smart{"\u00ae"}) in order to use TappyBLE devices or USB host operation for TappyUSB devices. For
          additional documentation, please see the repository on GitHub.
        </p>
        <div className="mt-5">
          <GitHubBadge href="https://github.com/TapTrack/TCMPTappy-Android" />
        </div>
      </section>

      <section>
        <h2 className="text-2xl md:text-3xl font-semibold text-slate-800 mb-4">Additional SDKs</h2>

        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-slate-800 uppercase tracking-wide mb-2">
              Apache Cordova
            </h3>
            <p className="text-slate-700 leading-relaxed">
              If you wish to use a Tappy reader with Cordova/PhoneGap, please refer to our Cordova{" "}
              <a
                href="https://taptrack.com/cordova/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#29abe2] hover:underline"
              >
                section
              </a>{" "}
              for further details.
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-slate-800 uppercase tracking-wide mb-2">Xamarin</h3>
            <p className="text-slate-700 leading-relaxed">
              If you wish to use a Tappy reader with Xamarin, please refer to our Xamarin{" "}
              <a
                href="https://taptrack.com/xamarin/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#29abe2] hover:underline"
              >
                section
              </a>{" "}
              for further details.
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-slate-800 uppercase tracking-wide mb-2">
              Legacy TappyBLE SDK
            </h3>
            <p className="text-slate-700 leading-relaxed">
              The previous version of the Android SDK only supported TappyBLE readers and thus has been deprecated
              in favour of the newer SDK. However, the code for this earlier SDK is still archived on GitHub for
              reference.
            </p>
            <div className="mt-4">
              <GitHubBadge href="https://github.com/TapTrack/TappyBLE" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function AppsTab() {
  return (
    <div className="space-y-10">
      <section>
        <h2 className="text-2xl md:text-3xl font-semibold text-slate-800 mb-4">Tappy Demo Application</h2>
        <p className="text-slate-700 leading-relaxed text-[16px]">
          If you wish to evaluate the Tappy on your Android device without doing development, the Tappy
          demonstration application supports many common Tappy operations and is freely available on the Play
          Store. The source code for this application can be found in the SDK{"\u2019"}s GitHub repository.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <GitHubBadge href="https://github.com/TapTrack/TCMPTappy-Android" />
        </div>
      </section>

      <section>
        <h2 className="text-2xl md:text-3xl font-semibold text-slate-800 mb-4">Tappy External NFC</h2>
        <p className="text-slate-700 leading-relaxed text-[16px]">
          If you only plan on using the Tappy to listen for tags being tapped, the Tappy External NFC application
          can help you get your app off the ground faster than if you had to integrate with the full SDK. The
          External NFC app allows you to search for and connect to the Tappies of your choosing, after which the
          app will broadcast an Intent when a tag is tapped on any of the connected readers. As a result, you will
          only have to add a very simple BroadcastReceiver to your application in order to start using the Tappy.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <GitHubBadge href="https://github.com/TapTrack/TappyExternalNfc" />
          <PlayStoreBadge href="https://play.google.com/store/apps/details?id=com.taptrack.roaring" />
        </div>
      </section>
    </div>
  );
}

function DocsTab() {
  return (
    <div>
      <h2 className="text-2xl md:text-3xl font-semibold text-slate-800 mb-4">Documentation</h2>
      <a
        href="https://docs.google.com/document/d/e/2PACX-1vTnzSyisbi6jD9mepVJsbgf6JL_qWjiIjGGIthRpULt2C5kkpuchm0nYa0U5NSgFOcpzzYzf6S9mGS6/pub?embedded=true"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 text-[#29abe2] hover:underline text-lg font-semibold"
      >
        Read the full documentation here.
        <ExternalLink className="w-4 h-4" />
      </a>
    </div>
  );
}
